import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useAuth } from './AuthContext';
import TaskForm from './TaskForm';
import { useKanban } from './KanbanContext';
import CommentSection from './CommentSection';
import ActivityTimeline from './ActivityTimeline';
import MoveTaskModal from './MoveTaskModal';
import ReassignTaskModal from './ReassignTaskModal.js';
import { downloadFileFromS3, uploadFileToS3 } from '../utils/lambdaApi';
import useEscapeKey from '../utils/useEscapeKey';
import { generateLocalTimestamp, getTaskAge, formatDaysElapsed, getTimeUntilArchival, formatTimeUntilArchival } from '../utils/taskUtils';
import { getActivityCount } from '../utils/activityHelpers';
import { getEmailDomainMark } from '../utils/config';

// Function to get file icon based on file type
const getFileIcon = (fileName) => {
  if (!fileName) return '📎';
  
  const name = fileName.toLowerCase();
  
  // PDFs
  if (name.endsWith('.pdf')) {
    return '📄';
  }
  
  // Word documents
  if (name.endsWith('.doc') || name.endsWith('.docx')) {
    return '📝';
  }
  
  // Excel
  if (name.endsWith('.xls') || name.endsWith('.xlsx')) {
    return '📊';
  }
  
  // Images
  if (name.endsWith('.jpg') || name.endsWith('.jpeg') || 
      name.endsWith('.png') || name.endsWith('.gif') || 
      name.endsWith('.bmp') || name.endsWith('.webp')) {
    return '🖼️';
  }
  
  // Default file icon
  return '📎';
};

function TaskCard({ task, onMoveTask, onDeleteTask, onUpdateTask }) {
  const { currentUser } = useAuth();
  const { updateTask, activeWorkspace, workspaces, addComment, deleteComment, editComment, getUserPermissions, canUserDoOnBoard } = useKanban();
  
  // Estados para modales - usando una clave única del task para persistir a través de re-renders
  const modalStateKey = `modal_${task.id || task.uuid}`;
  const [showDetails, setShowDetails] = useState(() => {
    return sessionStorage.getItem(`${modalStateKey}_details`) === 'true';
  });
  const [showEditForm, setShowEditForm] = useState(() => {
    return sessionStorage.getItem(`${modalStateKey}_edit`) === 'true';
  });
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [uploadProgress, setUploadProgress] = useState(0);
  const [preventClose, setPreventClose] = useState(false);
  const fileInputRef = useRef(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showMoveTaskModal, setShowMoveTaskModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  
  // Persistir estado de modales en sessionStorage solo cuando cambian
  useEffect(() => {
    const currentValue = sessionStorage.getItem(`${modalStateKey}_details`);
    const newValue = showDetails.toString();
    if (currentValue !== newValue) {
      sessionStorage.setItem(`${modalStateKey}_details`, newValue);
    }
  }, [showDetails, modalStateKey]);
  
  useEffect(() => {
    const currentValue = sessionStorage.getItem(`${modalStateKey}_edit`);
    const newValue = showEditForm.toString();
    if (currentValue !== newValue) {
      sessionStorage.setItem(`${modalStateKey}_edit`, newValue);
    }
  }, [showEditForm, modalStateKey]);
  
  // Limpiar sessionStorage cuando el componente se desmonte
  useEffect(() => {
    return () => {
      sessionStorage.removeItem(`${modalStateKey}_details`);
      sessionStorage.removeItem(`${modalStateKey}_edit`);
    };
  }, [modalStateKey]);
  
  // Funciones wrapper para manejar el estado persistente de modales
  const openDetailsModal = () => {
    setShowDetails(true);
  };
  
  const closeDetailsModal = () => {
    setShowDetails(false);
  };
  
  const openEditModal = () => {
    setShowEditForm(true);
  };
  
  const closeEditModal = () => {
    setShowEditForm(false);
  };
  
  // Cerrar modales con la tecla Escape
  useEscapeKey(() => {
    // Cerrar modales en orden de prioridad
    if (showMoveTaskModal) {
      setShowMoveTaskModal(false);
    } else if (showActivityModal) {
      setShowActivityModal(false);
    } else if (showEditForm) {
      closeEditModal();
    } else if (showDetails && !preventClose) {
      closeDetailsModal();
    } else if (showDeleteConfirmation) {
      setShowDeleteConfirmation(false);
    }
  }, showDetails || showEditForm || showActivityModal || showDeleteConfirmation || showMoveTaskModal || showReassignModal);
  
  // Manejar el botón de retroceso del navegador para modales
  useEffect(() => {
    // Solo agregar un estado al historial cuando se abre un modal
    if (showDetails || showEditForm || showActivityModal) {
      // Agregar una entrada al historial cuando se abre un modal
      window.history.pushState({ modal: true }, "");
      
      // Manejador para el evento popstate (botón atrás)
      const handlePopState = (event) => {
        // Cerrar modales en orden de prioridad
        if (showActivityModal) {
          setShowActivityModal(false);
        } else if (showEditForm) {
          closeEditModal();
        } else if (showDetails && !preventClose) {
          closeDetailsModal();
        }
        
        // Prevenir que el navegador vaya hacia atrás realmente
        if (event.state !== null) {
          window.history.pushState({ modal: true }, "");
        }
      };
      
      // Agregar el manejador de eventos
      window.addEventListener('popstate', handlePopState);
      
      // Limpiar el manejador al desmontar
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [showDetails, showEditForm, showActivityModal, preventClose]);
  
  // Formatear nombre de usuario desde email
  const formatUserName = (email) => {
    if (!email) return '';
    
    // Si no es un correo electrónico (no contiene @), devolver como está
    if (!email.includes('@')) return email;
    
    // Obtener la parte antes del @
    const namePart = email.split('@')[0];
    
    // Reemplazar puntos por espacios
    const nameWithSpaces = namePart.replace(/\./g, ' ');
    
    // Dividir el nombre completo en partes
    const nameParts = nameWithSpaces.split(' ');
    
    // Extraer el primer nombre y el primer apellido
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts[1] : '';
    
    // Capitalizar y combinar nombre y apellido
    const formattedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    const formattedLastName = lastName ? ' ' + lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase() : '';
    
    const mark = getEmailDomainMark(email);
    return formattedFirstName + formattedLastName + (mark ? ` ${mark}` : '');
  };
  
  // Función para obtener las iniciales del usuario para el avatar
  const getUserInitials = (email) => {
    if (!email) return '';
    
    // Si no es un correo electrónico, usar la primera letra
    if (!email.includes('@')) return email.charAt(0).toUpperCase();
    
    // Obtener la parte antes del @
    const namePart = email.split('@')[0];
    
    // Reemplazar puntos por espacios
    const nameWithSpaces = namePart.replace(/\./g, ' ');
    
    // Obtener las iniciales (primera letra de cada palabra)
    return nameWithSpaces.split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2); // Limitar a 2 caracteres
  };
  
  // Función helper centralizada para parsear timestamps
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    // Formato nuevo con segundos "HH:MM:SS DD-MM-YYYY" (con 1 espacio)
    const newFormat = /^\d{2}:\d{2}:\d{2} \d{2}-\d{2}-\d{4}$/;
    if (typeof timestamp === 'string' && newFormat.test(timestamp)) {
      const [timeStr, dateStr] = timestamp.split(' ');
      const [hours, minutes, seconds] = timeStr.split(':').map(num => parseInt(num, 10));
      const [day, month, year] = dateStr.split('-').map(num => parseInt(num, 10));
      return new Date(year, month - 1, day, hours, minutes, seconds);
    }
    
    // Formato estándar anterior "HH:MM DD-MM-YYYY" (con 1 espacio) - para compatibilidad
    const standardFormat = /^\d{2}:\d{2} \d{2}-\d{2}-\d{4}$/;
    if (typeof timestamp === 'string' && standardFormat.test(timestamp)) {
      const [timeStr, dateStr] = timestamp.split(' ');
      const [hours, minutes] = timeStr.split(':').map(num => parseInt(num, 10));
      const [day, month, year] = dateStr.split('-').map(num => parseInt(num, 10));
      return new Date(year, month - 1, day, hours, minutes, 0);
    }
    
    // Formato legacy con múltiples espacios
    if (typeof timestamp === 'string' && timestamp.includes('     ')) {
      const parts = timestamp.split('     ');
      if (parts.length === 2) {
        const [timeStr, dateStr] = parts;
        const [hours, minutes] = timeStr.split(':').map(num => parseInt(num, 10));
        const dateParts = dateStr.split(/[/-]/).map(num => parseInt(num, 10));
        if (dateParts.length === 3) {
          return new Date(dateParts[2], dateParts[1] - 1, dateParts[0], hours, minutes, 0);
        }
      }
    }
    
    // Fallback: intentar parsear como fecha normal
    const fallbackDate = new Date(timestamp);
    return isNaN(fallbackDate.getTime()) ? null : fallbackDate;
  };

  // Función para formatear fecha de finalización (oculta segundos en visualización)
  const formatCompletedDate = (completedDate) => {
    if (!completedDate) return 'Fecha no disponible';
    
    try {
      if (typeof completedDate === 'string') {
        // Si es formato ISO
        if (completedDate.includes('T')) {
          const date = new Date(completedDate);
          if (!isNaN(date.getTime())) {
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${hours}:${minutes} ${day}-${month}-${year}`;
          }
        }
        // Si es formato nuevo con segundos "HH:MM:SS DD-MM-YYYY", convertir a "HH:MM DD-MM-YYYY"
        else if (completedDate.match(/^\d{2}:\d{2}:\d{2} \d{2}-\d{2}-\d{4}$/)) {
          return completedDate.replace(/^(\d{2}:\d{2}):\d{2}(\s\d{2}-\d{2}-\d{4})$/, '$1$2');
        }
        // Si es formato legacy con 5 espacios
        else if (completedDate.includes('     ')) {
          return completedDate.replace('     ', ' ');
        }
        // Si ya está en formato correcto, devolverlo tal como está
        else {
          return completedDate;
        }
      } else {
        // Si es un objeto Date
        const date = new Date(completedDate);
        if (!isNaN(date.getTime())) {
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const year = date.getFullYear();
          return `${hours}:${minutes} ${day}-${month}-${year}`;
        }
      }
      
      return completedDate;
    } catch (e) {
      console.error("Error al formatear fecha de finalización:", e);
      return completedDate;
    }
  };

  // Función para formatear fechas en formato corto (para el botón)
  const formatShortDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseTimestamp(dateString);
      
      if (!date) {
        return 'Fecha actual';
      }
      
      // Mostrar día y mes abreviado
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short'
      });
    } catch (e) {
      console.error("Error al formatear fecha corta:", e);
      return 'Fecha actual';
    }
  };
  
  // Función para formatear la fecha de creación desde createdTimestamp
  const formatCreatedDate = (createdTimestamp) => {
    if (!createdTimestamp) return new Date().toLocaleDateString('es-ES');
    
    // Si createdTimestamp es un string con formato "HH:mm DD-MM-YYYY" (1 espacio)
    if (typeof createdTimestamp === 'string' && /^\d{2}:\d{2} \d{2}-\d{2}-\d{4}$/.test(createdTimestamp)) {
      return createdTimestamp; // Ya está en el formato correcto, devolverlo tal cual
    }
    
    // Si createdTimestamp es un string con formato "HH:mm     dd/MM/yyyy" (5 espacios)
    if (typeof createdTimestamp === 'string' && createdTimestamp.includes('     ')) {
      const [timeStr, dateStr] = createdTimestamp.split('     ');
      // Convertir al nuevo formato con un solo espacio
      return `${timeStr} ${dateStr.replace(/\//g, '-')}`;
    }
    
    try {
      // Si es un timestamp numérico o ISO string, convertirlo a fecha
      const date = new Date(createdTimestamp);
      
      // Verificar si es una fecha válida
      if (isNaN(date.getTime())) {
        const now = new Date();
        // Formatear la fecha actual con hora en formato "HH:MM DD-MM-YYYY"
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear();
        return `${hours}:${minutes} ${day}-${month}-${year}`;
      }
      
      // Formatear como "HH:MM DD-MM-YYYY" (formato español con hora)
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${hours}:${minutes} ${day}-${month}-${year}`;
    } catch (e) {
      console.error("Error al formatear fecha de creación:", e);
      // En caso de error, devolver la fecha actual en formato correcto
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const day = now.getDate().toString().padStart(2, '0');
      const month = (now.getMonth() + 1).toString().padStart(2, '0');
      const year = now.getFullYear();
      return `${hours}:${minutes} ${day}-${month}-${year}`;
    }
  };
  
  // Función para formatear correctamente las fechas de vencimiento
  // eslint-disable-next-line no-unused-vars
  const formatEndDate = (endDateString) => {
    if (!endDateString) return '';
    
    // Ya no es necesario formatear la fecha de vencimiento, devolverla como está
    return endDateString;
  };

  // Función para determinar el color del botón de fechas según el vencimiento
  const getDateButtonColor = (endDateString) => {
    // Si la tarea está completada, siempre mostrar verde
    if (task.status === 'done') {
      return '#009F6B'; // Verde para tareas completadas
    }
    
    if (!endDateString) return '#009F6B'; // Verde por defecto si no hay fecha de vencimiento
    
    try {
      const now = new Date();
      const endDate = parseTimestamp(endDateString);
      
      if (!endDate) {
        return '#009F6B'; // Verde por defecto para fechas inválidas
      }
      
      // Calcular diferencia en horas
      const diffHours = (endDate - now) / (1000 * 60 * 60);
      
      if (diffHours < 0) {
        return '#C73621'; // Rojo si ya venció
      } else if (diffHours <= 24) {
        return '#C7A500'; // Amarillo si quedan 24 horas o menos
      } else {
        return '#009F6B'; // Verde si está dentro del rango
      }
    } catch (e) {
      console.error("Error al determinar color de fecha:", e);
      return '#009F6B'; // Verde por defecto en caso de error
    }
  };
  
  // Función para obtener el mensaje del tooltip según el estado de vencimiento
  const getDateTooltipMessage = (endDateString) => {
    // Si la tarea está completada, mostrar mensaje de completada
    if (task.status === 'done' && task.completedDate) {
      const completedDate = parseTimestamp(task.completedDate);
      if (completedDate) {
        return `✅ Tarea completada el ${completedDate.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })} a las ${completedDate.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        })}`;
      }
      return "✅ Tarea completada";
    }
    
    if (!endDateString) return "✅ Plazo sin fecha de vencimiento";
    
    try {
      const now = new Date();
      const endDate = parseTimestamp(endDateString);
      
      if (!endDate) {
        return "✅ Plazo sin fecha de vencimiento";
      }
      
      // Calcular diferencia en horas
      const diffHours = (endDate - now) / (1000 * 60 * 60);
      const diffDays = Math.floor(diffHours / 24);
      
      const dateFormatted = endDate.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const timeFormatted = endDate.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      });
      
      if (diffHours < 0) {
        const overdueDays = Math.abs(diffDays);
        if (overdueDays === 0) {
          return `⚠️ Venció hoy (${dateFormatted} a las ${timeFormatted})`;
        } else if (overdueDays === 1) {
          return `⚠️ Venció ayer (${dateFormatted} a las ${timeFormatted})`;
        } else {
          return `⚠️ Venció hace ${overdueDays} días (${dateFormatted} a las ${timeFormatted})`;
        }
      } else if (diffHours <= 24) {
        if (diffHours < 1) {
          const remainingMinutes = Math.floor((diffHours * 60));
          return `⏰ Vence en ${remainingMinutes} minutos (${dateFormatted} a las ${timeFormatted})`;
        } else {
          const remainingHours = Math.floor(diffHours);
          return `⏰ Vence en ${remainingHours} horas (${dateFormatted} a las ${timeFormatted})`;
        }
      } else {
        if (diffDays === 1) {
          return `📅 Vence mañana (${dateFormatted} a las ${timeFormatted})`;
        } else {
          return `📅 Vence en ${diffDays} días (${dateFormatted} a las ${timeFormatted})`;
        }
      }
    } catch (e) {
      console.error("Error al determinar mensaje de tooltip:", e);
      return "✅ Plazo establecido";
    }
  };

  // Set up drag functionality
  const handleDragStart = (e) => {
    e.dataTransfer.setData("taskId", task.id);
    e.dataTransfer.setData("sourceStatus", task.status);
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'todo': return 'Pendiente';
      case 'inProgress': return 'En progreso';
      case 'done': return 'Completado';
      default: return status;
    }
  };

  // Función para truncar la descripción a un máximo de caracteres
  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  // Descripción truncada para mostrar en la tarjeta
  // eslint-disable-next-line no-unused-vars
  const truncatedDescription = truncateText(task.description, 250);
  // eslint-disable-next-line no-unused-vars
  const hasLongDescription = task.description && task.description.length > 250;

  // Función para asignar la tarea al usuario actual y moverla a En Progreso
  const assignTaskToMe = (e) => {
    e.stopPropagation();
    if (onMoveTask && currentUser) {
      // Crear timestamp y obtener datos del usuario
      const timestamp = generateLocalTimestamp(); // Usar timestamp local
      const userEmail = currentUser.email || 'Usuario';
      
      // Solo actualizar el usuario si la tarea no tiene ya un usuario asignado
      if (!task.assignee || task.assignee.trim() === '') {
        // Crear entrada para el historial de actividad unificado - cambio de asignado
        const assigneeActivityEntry = {
          timestamp: timestamp,
          type: "ASSIGNEE_CHANGE",
          field: "assignee",
          oldValue: null,
          newValue: userEmail,
          updatedBy: userEmail
        };
        
        // Crear entrada para el historial de actividad unificado - cambio de estado
        const statusActivityEntry = {
          timestamp: timestamp,
          type: "STATUS_CHANGE",
          field: "status",
          oldValue: task.status,
          newValue: "inProgress",
          updatedBy: userEmail
        };
        
        // Actualizar el historial de actividad
        const activityHistory = [
          ...(task.activityHistory || []), 
          assigneeActivityEntry,
          statusActivityEntry
        ];
        
        // Crear objeto de tarea actualizada con todos los cambios
        const updatedTask = {
          ...task,
          assignee: userEmail,
          status: "inProgress",
          activityHistory: activityHistory,
          updatedAt: timestamp
        };
        
        // Utilizar updateTask en lugar de onMoveTask para tener control total
        if (updateTask) {
          updateTask(activeWorkspace, task.id, updatedTask);
        } else {
          // Si no hay updateTask disponible, usar onMoveTask como fallback
          onMoveTask(task.id, task.status, 'inProgress', updatedTask);
        }
      } else {
        // Si ya hay un asignado, solo actualizar el estado
        onMoveTask(task.id, task.status, 'inProgress', task);
      }
    }
  };

  // Función para manejar la actualización de la tarea
  const handleUpdateTask = (updatedTask) => {
    if (updateTask) {
      // Verificar si el historial de actividades se está manteniendo
      
      // Crear una versión actualizada de la tarea local para mantener la UI actualizada
      const updatedLocalTask = {
        ...task,
        ...updatedTask,
        id: task.id // Asegurarnos de mantener el ID
      };
      
      // Actualizar en el backend
      updateTask(activeWorkspace, task.id, updatedTask);
      
      // Actualizar la UI inmediatamente si tenemos onUpdateTask disponible
      if (onUpdateTask) {
        onUpdateTask(updatedLocalTask);
      }
      
      // Cerrar el formulario de edición y abrir el modal de vista de la tarea
      setShowEditForm(false);
      setShowDetails(true);
      
      // Mostrar mensaje de éxito temporal
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    }
  };

  // Función para mostrar la confirmación de eliminación
  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  // Función para confirmar la eliminación de la tarea
  const confirmDelete = () => {
    if (onDeleteTask) {
      onDeleteTask(task.id);
      closeDetailsModal();
      setShowDeleteConfirmation(false);
    }
  };

  // Función para cancelar la eliminación
  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  // Función para abrir el modal de mover tarea
  const handleMoveTaskClick = () => {
    setShowMoveTaskModal(true);
  };

  // Función para cerrar el modal de mover tarea
  const closeMoveTaskModal = () => {
    setShowMoveTaskModal(false);
  };

  // Función para abrir el modal de reasignar
  const handleReassignClick = () => {
    setShowReassignModal(true);
  };

  // Función para cerrar el modal de reasignar
  const closeReassignModal = () => {
    setShowReassignModal(false);
  };

  // Función para manejar la reasignación
  const handleReassign = async (newAssigneeEmail) => {
    if (!newAssigneeEmail || newAssigneeEmail === task.assignee) {
      return;
    }

    const timestamp = generateLocalTimestamp();
    const userEmail = currentUser?.email || 'Usuario';
    
    // Crear entrada para el historial de actividad
    const reassignActivityEntry = {
      timestamp: timestamp,
      type: "ASSIGNEE_CHANGE",
      field: "assignee",
      oldValue: task.assignee || null,
      newValue: newAssigneeEmail,
      updatedBy: userEmail
    };
    
    // Actualizar el historial de actividad
    const activityHistory = [
      ...(task.activityHistory || []), 
      reassignActivityEntry
    ];
    
    // Crear objeto de tarea actualizada
    const updatedTask = {
      ...task,
      assignee: newAssigneeEmail,
      activityHistory: activityHistory,
      updatedAt: timestamp
    };
    
    // Actualizar la tarea
    if (updateTask) {
      await updateTask(activeWorkspace, task.id, updatedTask);
    }
    
    closeReassignModal();
  };

  // Función para manejar el éxito al mover la tarea
  const handleMoveTaskSuccess = (targetWorkspaceName) => {
    // Refrescar la página para ver los cambios
    window.location.reload();
  };

  // Verificar si la tarea puede ser asignada (solo las pendientes)
  const canBeAssigned = task.status === 'todo';
  
  // Verificar si la tarea ya tiene un asignado
  const hasAssignee = task.assignee && task.assignee.trim() !== '';

  const userPermissions = getUserPermissions();
  const canMoveTask = userPermissions.sendTasks;
  const canEditTask = canUserDoOnBoard('editTasks');
  const canDeleteTask = canUserDoOnBoard('deleteTasks');
  const canReassignTask = canUserDoOnBoard('reassignTasks');
  const canAddComments = canUserDoOnBoard('addComments');
  const canEditDeleteComments = canUserDoOnBoard('editDeleteComments');
  const canViewActivity = canUserDoOnBoard('viewActivityHistory');
  const canUploadAttachments = canUserDoOnBoard('uploadAttachments');
  const canMoveBetweenStates = canUserDoOnBoard('moveBetweenStates');

  // Obtener etiquetas y asegurar que siempre sea un array, procesando posibles cadenas con comas
  const tags = useMemo(() => {
    // Si no hay etiquetas, devolver un array vacío
    if (!task.tags) return [];
    
    // Si ya es un array, procesarlo para eliminar posibles valores problemáticos
    if (Array.isArray(task.tags)) {
      return task.tags
        .filter(tag => tag && typeof tag === 'string') // Filtrar solo strings válidos
        .flatMap(tag => {
          // Si una etiqueta contiene comas, dividirla en múltiples etiquetas
          // Siempre devolver array: flatMap con string escalar itera carácter a carácter (bug)
          return tag.includes(',')
            ? tag.split(',').map(t => t.trim().toUpperCase()).filter(Boolean)
            : [tag.trim().toUpperCase()].filter(Boolean);
        })
        .filter(Boolean); // Eliminar etiquetas vacías después de trimear
    }
    
    // Si es una cadena, procesarla como una lista separada por comas
    if (typeof task.tags === 'string') {
      return task.tags.split(',').map(tag => tag.trim().toUpperCase()).filter(Boolean);
    }
    
    // En cualquier otro caso, devolver array vacío
    return [];
  }, [task.tags]);

  // Función para añadir un comentario a la tarea
  const handleAddComment = (comment) => {
    if (addComment) {
      // Si el comentario incluye archivos adjuntos, también actualizar la lista de archivos de la tarea
      if (comment.attachments && comment.attachments.length > 0) {
        const newFiles = comment.attachments.map(att => att.name);
        const existingFiles = task.files || [];
        
        // Solo agregar archivos que no existan ya
        const filesToAdd = newFiles.filter(file => !existingFiles.includes(file));
        
        if (filesToAdd.length > 0) {
          const updatedFiles = [...existingFiles, ...filesToAdd];
          
          const timestamp = generateLocalTimestamp();
          const userEmail = currentUser?.email || 'Usuario';
          
          // Crear entrada para el historial de actividad unificado
          const activityEntry = {
            timestamp: timestamp,
            type: "ATTACHMENT",
            field: "files",
            oldValue: existingFiles.join(', '),
            newValue: updatedFiles.join(', '),
            updatedBy: userEmail
          };
          
          // Actualizar el historial de actividad
          const updatedActivityHistory = [...(task.activityHistory || []), activityEntry];
          
          // Crear objeto de actualización
          const updates = {
            files: updatedFiles,
            activityHistory: updatedActivityHistory,
            updatedAt: timestamp
          };
          
          // Actualizar la tarea en el backend
          updateTask(activeWorkspace, task.id, updates);
        }
      }
      
      addComment(activeWorkspace, task.id, comment);
    }
  };

  // Función para eliminar un comentario
  const handleDeleteComment = (commentId) => {
    if (deleteComment) {
      deleteComment(activeWorkspace, task.id, commentId);
    }
  };

  // Función para editar un comentario
  const handleEditComment = (commentId, newText) => {
    if (editComment) {
      editComment(activeWorkspace, task.id, commentId, newText);
    }
  };

  // Handle file selection for attachment
  // eslint-disable-next-line no-unused-vars
  const handleFileChange = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevenir que el evento se propague
    
    const files = Array.from(e.target.files);
    
    // Filtrar para archivos permitidos: PDFs, Word, e imágenes
    const allowedFiles = files.filter(file => {
      const fileType = file.type.toLowerCase();
      const fileName = file.name.toLowerCase();
      
      // PDFs
      if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
        return true;
      }
      
      // Documentos Word
      if (fileType === 'application/msword' || 
          fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          fileName.endsWith('.doc') || 
          fileName.endsWith('.docx')) {
        return true;
      }
      
      // Excel
      if (fileType === 'application/vnd.ms-excel' ||
          fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          fileName.endsWith('.xls') ||
          fileName.endsWith('.xlsx')) {
        return true;
      }
      
      // Imágenes
      if (fileType.startsWith('image/') || 
          fileName.endsWith('.jpg') || 
          fileName.endsWith('.jpeg') || 
          fileName.endsWith('.png') || 
          fileName.endsWith('.gif') || 
          fileName.endsWith('.bmp') || 
          fileName.endsWith('.webp')) {
        return true;
      }
      
      return false;
    });
    
    if (allowedFiles.length === 0) {
      alert('Solo se permiten archivos PDF, Word (.doc, .docx), Excel (.xls, .xlsx) e imágenes (.jpg, .jpeg, .png, .gif, .bmp, .webp).');
      return;
    }
    
    // Activar la protección contra cierre del modal
    setPreventClose(true);
    
    // Start upload process
    setIsUploading(true);
    setUploadProgress(0);
    
    const newUploadedFileNames = [];
    
    // Process each file
    for (let i = 0; i < allowedFiles.length; i++) {
      const file = allowedFiles[i];
      
      try {
        setUploadProgress(Math.round((i / allowedFiles.length) * 50));
        
        const result = await uploadFileToS3(file);
        let fileRef = file.name;
        if (result.body && typeof result.body === 'string') {
          try {
            const parsedBody = JSON.parse(result.body);
            if (parsedBody.key) fileRef = parsedBody.key;
          } catch (e) {
            console.error('Error al analizar respuesta de API:', e);
          }
        }
        newUploadedFileNames.push(fileRef);
      } catch (error) {
        console.error('Error al subir PDF:', error);
        alert(`Error al subir ${file.name}: ${error.message}`);
      }
    }
    
    // Update task with new files
    if (newUploadedFileNames.length > 0) {
      const updatedFiles = [...(task.files || []), ...newUploadedFileNames];
      const timestamp = generateLocalTimestamp(); // Usar timestamp local
      const userEmail = currentUser?.email || 'Usuario';
      
      // Crear entrada para el historial de actividad unificado
      const activityEntry = {
        timestamp: timestamp,
        type: "ATTACHMENT",
        field: "files",
        oldValue: task.files ? task.files.join(', ') : '',
        newValue: updatedFiles.join(', '),
        updatedBy: userEmail
      };
      
      // Crear una versión actualizada de la tarea con el historial de actividad actualizado
      const updatedActivityHistory = [...(task.activityHistory || []), activityEntry];
      
      // Crear objeto de actualización
      const updates = {
        files: updatedFiles,
        activityHistory: updatedActivityHistory,
        updatedAt: timestamp
      };
      
      // Crear una versión actualizada de la tarea que mantendremos en el estado local
      // hasta que se complete la actualización en el backend
      const updatedLocalTask = {
        ...task,
        ...updates
      };
      
      // Actualizar la tarea en el backend
      try {
        await updateTask(activeWorkspace, task.id, updates);
        
        // Esperar un momento para que la actualización se propague
        setTimeout(() => {
          // Forzar una recarga de la tarea desde el estado actualizado
          const updatedTaskFromState = workspaces[activeWorkspace]?.tasks?.find(t => t.id === task.id);
          
          if (updatedTaskFromState) {
            // Forzar actualización del estado local para mostrar el archivo inmediatamente
            if (onUpdateTask) {
              // Solo si tenemos acceso a la función onUpdateTask 
              // Esto mantendrá el modal abierto con la tarea actualizada
              onUpdateTask(updatedTaskFromState);
            }
          } else {
            console.error('No se pudo encontrar la tarea actualizada en el estado global');
            
            // Usar la versión local como fallback
            if (onUpdateTask) {
              onUpdateTask(updatedLocalTask);
            }
          }
        }, 500); // Esperar medio segundo para que la recarga de tareas ocurra
      } catch (error) {
        console.error('Error al actualizar la tarea:', error);
        alert('Hubo un error al guardar los archivos. Por favor, inténtalo de nuevo.');
      }
    }
    
    // Reset file input and update progress
    e.target.value = null;
    setUploadProgress(100);
    
            // Asegurarnos de que el modal permanezca abierto y se restablezca el progreso
        setTimeout(() => {
          setUploadProgress(0);
          setIsUploading(false);
          setPreventClose(false);
        }, 200);
  };

  // Function to trigger file input click
  // eslint-disable-next-line no-unused-vars
  const handleAttachButtonClick = (e) => {
    // Prevenir que el evento se propague y cierre el modal
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Activar protección contra cierre del modal
    setPreventClose(true);
    
    // Abrir el selector de archivos
    fileInputRef.current.click();
    
    // Desactivar protección después de un momento si no se selecciona archivo
    setTimeout(() => {
      if (!isUploading) {
        setPreventClose(false);
      }
    }, 5000);
  };

  // Function to get border color based on status
  const getBorderColor = (status) => {
    switch (status) {
      case 'todo': return '#C73621'; // Pendiente - rojo
      case 'inProgress': return '#C7A500'; // En progreso - amarillo
      case 'done': return '#009F6B'; // Completado - verde
      default: return '#A3CB3F'; // Color por defecto
    }
  };

  // Función para limpiar la descripción de URLs y fechas innecesarias
  const cleanDescription = (description) => {
    if (!description) return '';
    
    // Eliminar Start Date, End Date y URLs de la descripción de forma más completa
    let cleaned = description.replace(/Start Date:.*?(?=\n|$)/g, '');
    cleaned = cleaned.replace(/End Date:.*?(?=\n|$)/g, '');
    cleaned = cleaned.replace(/https?:\/\/[^\s]+/g, '');
    cleaned = cleaned.replace(/URL:/g, '');
    
    // Eliminar líneas vacías y espacios extra
    cleaned = cleaned.replace(/\n\s*\n/g, '\n');
    cleaned = cleaned.trim();
    
    return cleaned;
  };

  // Dentro del componente TaskCard, antes del return
  // eslint-disable-next-line no-unused-vars
  const endDate = task.endDate || (task.description && task.description.match(/End Date: ([^\n]*)/)?.[1]);
  // eslint-disable-next-line no-unused-vars
  const formattedEndDate = task.endDate; // Usar directamente task.endDate sin formateo
  
  // Extraer fechas Start Date y End Date de la descripción
  const extractDatesFromDescription = () => {
    if (!task.description) return { startDate: null, endDate: null };
    
    const startDateMatch = task.description.match(/Start Date:?\s*([^\n]*)/i);
    const endDateMatch = task.description.match(/End Date:?\s*([^\n]*)/i);
    
    return {
      startDate: startDateMatch ? startDateMatch[1].trim() : null,
      endDate: endDateMatch ? endDateMatch[1].trim() : null
    };
  };
  
  // eslint-disable-next-line no-unused-vars
  const extractedDates = extractDatesFromDescription();
  const finalEndDate = task.endDate;
  
  // Verificar si tenemos una fecha de vencimiento válida
  

  // Procesar URLs del campo url
  // Asegurarse de que urls sea siempre un array
  const urls = Array.isArray(task.url) ? task.url : (task.url ? [task.url] : []);
  
  // Descripción sin URLs
  // eslint-disable-next-line no-unused-vars
  const descriptionWithoutUrls = cleanDescription(task.description);

  // Función para obtener la fecha de subida de un archivo desde el historial de actividades
  const getFileUploadDate = (fileName) => {
    if (!task.activityHistory || !Array.isArray(task.activityHistory)) {
      return null;
    }

    // Buscar en el historial de actividades las entradas de tipo ATTACHMENT
    const attachmentActivities = task.activityHistory.filter(activity => 
      activity.type === 'ATTACHMENT' && activity.field === 'files'
    );

    // Buscar la actividad donde se agregó este archivo específico
    for (const activity of attachmentActivities) {
      const newFiles = activity.newValue ? activity.newValue.split(', ') : [];
      const oldFiles = activity.oldValue ? activity.oldValue.split(', ') : [];
      
      // Verificar si este archivo fue agregado en esta actividad
      const addedFiles = newFiles.filter(file => !oldFiles.includes(file));
      
      if (addedFiles.includes(fileName)) {
        return activity.timestamp;
      }
    }

    return null;
  };

  // Función para obtener quién subió un archivo específico
  const getFileUploader = (fileName) => {
    if (!task.activityHistory || !Array.isArray(task.activityHistory)) {
      return null;
    }

    // Buscar en el historial de actividades las entradas de tipo ATTACHMENT
    const attachmentActivities = task.activityHistory.filter(activity => 
      activity.type === 'ATTACHMENT' && activity.field === 'files'
    );

    // Buscar la actividad donde se agregó este archivo específico
    for (const activity of attachmentActivities) {
      const newFiles = activity.newValue ? activity.newValue.split(', ') : [];
      const oldFiles = activity.oldValue ? activity.oldValue.split(', ') : [];
      
      // Verificar si este archivo fue agregado en esta actividad
      const addedFiles = newFiles.filter(file => !oldFiles.includes(file));
      
      if (addedFiles.includes(fileName)) {
        return activity.updatedBy;
      }
    }

    return null;
  };

  // Función para obtener archivos que no están asociados a comentarios
  const getStandaloneFiles = () => {
    if (!task.files || !Array.isArray(task.files)) {
      return [];
    }

    // Obtener todos los archivos que están en comentarios
    const filesInComments = new Set();
    if (task.comments && Array.isArray(task.comments)) {
      task.comments.forEach(comment => {
        if (comment.attachments && Array.isArray(comment.attachments)) {
          comment.attachments.forEach(attachment => {
            filesInComments.add(attachment.name);
          });
        }
      });
    }

    // Filtrar archivos que no están en comentarios
    return task.files.filter(file => !filesInComments.has(file));
  };

  // Función para formatear la fecha de subida (solo fecha y HH:MM)
  const formatUploadDate = (timestamp) => {
    if (!timestamp) return null;
    
    try {
      const date = parseTimestamp(timestamp);
      if (!date) return null;
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (e) {
      console.error("Error al formatear fecha de subida:", e);
      return null;
    }
  };

  // Función para contar archivos adjuntos independientes
  const getAttachmentsCount = () => {
    const standaloneFiles = getStandaloneFiles();
    return standaloneFiles.length;
  };
  
  // Función para contar URLs
  const getUrlsCount = () => {
    return urls.length;
  };

  // Formatear el nombre del asignado y creador
  const formattedAssignee = formatUserName(task.assignee);
  const formattedCreatedBy = formatUserName(task.createdBy);

  // La función getActivityCount ahora se importa desde activityHelpers
  const getTaskActivityCount = () => {
    return getActivityCount(task);
  };


  // Función para marcar una tarea como completada
  const handleCompleteTask = (e) => {
    e.stopPropagation();
    if (onMoveTask) {
      const timestamp = generateLocalTimestamp(); // Usar timestamp local
      const userEmail = currentUser?.email || 'Usuario';
      
      // Crear entrada para el historial de actividad unificado - cambio de estado
      const statusActivityEntry = {
        timestamp: timestamp,
        type: "STATUS_CHANGE",
        field: "status",
        oldValue: task.status,
        newValue: "done",
        updatedBy: userEmail
      };
      
      // Actualizar el historial de actividad
      const activityHistory = [
        ...(task.activityHistory || []), 
        statusActivityEntry
      ];
      
      // Como ya tenemos el timestamp en formato local, usarlo directamente para completedDate
      const formattedCompletionDate = timestamp;
      
      // Crear objeto de tarea actualizada con todos los cambios
      const updatedTask = {
        ...task,
        status: 'done',
        completedDate: formattedCompletionDate, // Usar el timestamp local directamente
        // No modificamos endDate para conservar la fecha de vencimiento original
        activityHistory: activityHistory,
        updatedAt: timestamp
      };
      
      // Usar onMoveTask con confirmación, igual que en drag and drop
      onMoveTask(task.id, task.status, 'done', updatedTask);
    }
  };

  return (
    <>
      <div 
        className="task-card"
        style={{ borderLeftColor: getBorderColor(task.status) }}
        draggable={canMoveBetweenStates}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={() => openDetailsModal()}
      >
        <div className="task-content">
          <div className="task-header">
            <h3 className="task-title">{task.title}</h3>
            <div className="task-header-meta">
              {task.createdBy && (
                <div className="task-creation-date">Por: {formattedCreatedBy}</div>
              )}
              <div className="task-age-counter">
                📅 {formatDaysElapsed(getTaskAge(task))}
              </div>
            </div>
          </div>
          
          <div className="task-meta">
            {hasAssignee && (
              <div className="user-chip">
                <div className="user-avatar" data-initial={getUserInitials(task.assignee).charAt(0)}>{getUserInitials(task.assignee)}</div>
                <div className="user-name">{formattedAssignee}</div>
              </div>
            )}
          </div>
          
          {/* Mostrar etiquetas si existen */}
          {tags.length > 0 && (
            <div className="task-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
              {tags.map((tag, index) => (
                <span key={index} className="task-tag">{tag}</span>
              ))}
            </div>
          )}
          
          {/* Mostrar información de archivado para tareas completadas */}
          {task.status === 'done' && (
            <div className="task-archival-preview" style={{ 
              fontSize: '12px', 
              color: '#666', 
              marginBottom: '6px',
              padding: '4px 8px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #e9ecef'
            }}>
              <span style={{ marginRight: '6px' }}>⏳</span>
              {(() => {
                if (task.ArchivedDate) {
                  return "📦 Tarea archivada";
                }
                
                const archivalInfo = getTimeUntilArchival(task);
                if (archivalInfo) {
                  if (archivalInfo.shouldBeArchived) {
                    return "🔄 Listo para archivar";
                  } else {
                    return `Se archivará en ${formatTimeUntilArchival(archivalInfo)}`;
                  }
                } else {
                  return "Sin fecha de finalización";
                }
              })()}
            </div>
          )}
          
          <div className="task-footer">
            <div className="task-footer-buttons">
              {/* Botón de fechas */}
              <button 
                className="date-info-btn"
                style={{ backgroundColor: getDateButtonColor(finalEndDate) }}
                data-tooltip={getDateTooltipMessage(finalEndDate)}
                onClick={(e) => {
                  e.stopPropagation();
                  openDetailsModal();
                }}
              >
                <span>
                {task.title === 'date' && task.status === 'todo' ? 
                  // Caso especial para la tarea "date" con problemas de fecha
                  `${new Date().getDate()} ${['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][new Date().getMonth()]}` :
                  task.status === 'done' && task.completedDate ?
                  `Vence: ${formatShortDate(task.endDate)}` :
                  task.endDate ? 
                  `Vence: ${formatShortDate(task.endDate)}` : 
                  `Creada: ${formatShortDate(task.createdTimestamp)}`
                }
                </span>
              </button>
              
              {/* Indicadores compactos para comentarios, adjuntos y enlaces */}
              <div className="task-metadata-indicators">
                {task.comments && task.comments.length > 0 && (
                  <div className="task-indicator">
                    <span className="task-indicator-icon">💬</span>
                    <span>{task.comments.length}</span>
                  </div>
                )}
                
                {getAttachmentsCount() > 0 && (
                  <div className="task-indicator">
                    <span className="task-indicator-icon">📎</span>
                    <span>{getAttachmentsCount()}</span>
                  </div>
                )}
                
                {getUrlsCount() > 0 && (
                  <div className="task-indicator">
                    <span className="task-indicator-icon">🔗</span>
                    <span>{getUrlsCount()}</span>
                  </div>
                )}
              </div>
              
              {task.status === 'inProgress' && canMoveBetweenStates && (
                <button 
                  className="complete-task-btn"
                  onClick={handleCompleteTask}
                  title="Marcar como completada"
                >
                  Completar ✓
                </button>
              )}
              
              {/* Botón Asignarme (movido a la derecha) */}
              {currentUser && canBeAssigned && !hasAssignee && (
                <button
                  className="assign-to-me-btn"
                  onClick={assignTaskToMe}
                  title="Asignarme esta tarea"
                  aria-label={`Asignarme la tarea ${task.title}`}
                >
                  Asignarme
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

                    {showDetails && !showEditForm && (
                <div className="task-modal-overlay" onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!preventClose && !isUploading) {
                    closeDetailsModal();
                  }
                }}>
          <div className="task-modal" onClick={(e) => e.stopPropagation()}>
            <div className="task-modal-header">
              <h3>{task.title}</h3>
              {(isUploading || preventClose) && (
                <div className="operation-indicator">
                  <span className="loading-spinner small"></span>
                  <span className="operation-text">
                    {isUploading ? 'Subiendo archivo...' : 'Operación en progreso...'}
                  </span>
                </div>
              )}
              <div className="task-modal-actions">
                {task.status !== 'done' && canEditTask && (
                  <button 
                    className="header-action-btn edit-task-btn"
                    onClick={() => {
                      openEditModal();
                      closeDetailsModal();
                    }}
                  >
                    Editar
                  </button>
                )}
                {canReassignTask && (
                  <button 
                    className="header-action-btn reassign-task-btn"
                    onClick={handleReassignClick}
                    title="Reasignar tarea"
                  >
                    Reasignar
                  </button>
                )}
                {canMoveTask && (
                  <button 
                    className="header-action-btn move-task-btn"
                    onClick={handleMoveTaskClick}
                  >
                    Mover a tablero
                  </button>
                )}
                {canDeleteTask && (
                  <button 
                    className="header-action-btn delete-task-btn"
                    onClick={handleDeleteClick}
                  >
                    Eliminar
                  </button>
                )}
                <button 
                  className="header-action-btn close-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!preventClose && !isUploading) {
                      closeDetailsModal();
                    }
                  }}
                  disabled={preventClose || isUploading}
                  style={{ opacity: (preventClose || isUploading) ? 0.5 : 1 }}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="task-modal-content">
              <div className="task-panel-left">
                <div className="task-section">
                  <div className="task-section-header">
                    <h5 className="task-section-title">Descripción</h5>
                  </div>
                  <div className="task-section-content">
                    <p className="descripcion-contenido">{cleanDescription(task.description) || 'Sin descripción'}</p>
                  </div>
                </div>
                
                {/* Sección de Enlaces que muestra URLs extraídas y el campo task.url */}
                {urls.length > 0 && (
                  <div className="task-section enlaces-seccion">
                    <div className="task-section-header">
                      <h5 className="task-section-title">Enlaces</h5>
                    </div>
                    <div className="task-section-content">
                      {/* Mostrar todas las URLs */}
                      {urls.map((url, index) => (
                        <div className="url-link" key={index}>
                          <span className="url-icon" style={{ fontSize: '1.2rem' }}>🔗</span>
                          <a 
                            href={url} 
                            target="_blank"
                            rel="noopener noreferrer" 
                            className="enlace-item"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {url}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Sección de archivos adjuntos independientes (no asociados a comentarios) */}
                {(() => {
                  const standaloneFiles = getStandaloneFiles();
                  return standaloneFiles.length > 0 && (
                    <div className="task-section">
                      <div className="task-section-header">
                        <h5 className="task-section-title">Archivos adjuntos</h5>
                      </div>
                      <div className="task-section-content">
                        <div className="task-attachments">
                          {standaloneFiles.map((file, index) => {
                            const uploadDate = getFileUploadDate(file);
                            const formattedUploadDate = formatUploadDate(uploadDate);
                            
                            // Buscar quién subió este archivo en el historial de actividades
                            const fileUploader = getFileUploader(file);
                            
                            return (
                              <div key={index} className="attachment-item">
                                <div className="attachment-info">
                                  <span className="attachment-icon">{getFileIcon(file)}</span>
                                  <button 
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      downloadFileFromS3(file);
                                    }}
                                    className="attachment-link"
                                  >
                                    {file}
                                  </button>
                                </div>
                                {fileUploader && (
                                  <div className="attachment-uploader">
                                    <span className="uploader-icon">👤</span>
                                    <span className="uploader-name">Subido por: {formatUserName(fileUploader)}</span>
                                  </div>
                                )}
                                {formattedUploadDate && (
                                  <div className="attachment-date">
                                    <span className="date-icon">📅</span>
                                    <span className="date-text">{formattedUploadDate}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                <div className="task-section">
                  <div className="task-section-content">
                    <CommentSection
                      taskId={task.uuid || task.id}
                      comments={task.comments || []}
                      onAddComment={canAddComments ? handleAddComment : undefined}
                      onDeleteComment={canEditDeleteComments ? handleDeleteComment : undefined}
                      onEditComment={canEditDeleteComments ? handleEditComment : undefined}
                      onAddAttachment={canUploadAttachments ? () => {} : undefined}
                      canUploadAttachment={canUploadAttachments}
                    />
                  </div>
                </div>
              </div>
              
              <div className="task-panel-right">
                <div className="task-meta-section">
                  <div className="task-section">
                    <div className="task-section-header">
                      <h5 className="task-section-title">Estado</h5>
                      <div className={`task-meta-badge status-${task.status}`}>
                        {getStatusText(task.status)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="task-section">
                    <div className="task-section-header">
                      <h5 className="task-section-title">Responsable</h5>
                    </div>
                    <div className="task-section-content">
                      {hasAssignee ? (
                        <div className="user-chip">
                          <div className="user-avatar" data-initial={getUserInitials(task.assignee).charAt(0)}>{getUserInitials(task.assignee)}</div>
                          <div className="user-name">{formattedAssignee}</div>
                        </div>
                      ) : (
                        currentUser && canBeAssigned ? (
                          <button
                            className="assign-to-me-btn"
                            onClick={assignTaskToMe}
                            title="Asignarme esta tarea"
                            aria-label={`Asignarme la tarea ${task.title}`}
                          >
                            Asignarme esta tarea
                          </button>
                        ) : (
                          <span>Sin responsable</span>
                        )
                      )}
                    </div>
                  </div>
                  
                  {/* Mostrar etiquetas en la vista detallada */}
                  {tags.length > 0 && (
                    <div className="task-section">
                      <div className="task-section-header">
                        <h5 className="task-section-title">Etiquetas</h5>
                      </div>
                      <div className="task-tags-detail">
                        {tags.map((tag, index) => (
                          <span key={index} className="task-tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {task.createdTimestamp && (
                    <div className="task-section">
                      <div className="task-section-header">
                        <h5 className="task-section-title">Fecha de creación</h5>
                      </div>
                      <div className="task-section-content">
                        <p>{task.title === 'date' && task.status === 'todo' ? 
                          // Caso especial para la tarea "date" con problemas de fecha
                          new Date().toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          }) :
                          formatCreatedDate(task.createdTimestamp)
                        }</p>
                      </div>
                    </div>
                  )}

                  {/* Sección de días transcurridos */}
                  <div className="task-section">
                    <div className="task-section-header">
                      <h5 className="task-section-title">Tiempo transcurrido</h5>
                    </div>
                    <div className="task-section-content">
                      <div className="task-age-display">
                        <span className="task-age-icon">📅</span>
                        <span className="task-age-text">{formatDaysElapsed(getTaskAge(task))}</span>
                        <span className="task-age-detail">desde la creación</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Fecha de vencimiento movida aquí */}
                  {task.endDate && (
                    <div className="task-section">
                      <div className="task-section-header">
                        <h5 className="task-section-title">
                          Fecha de Vencimiento
                        </h5>
                      </div>
                      <div className="task-section-content">
                        <p className="fecha-vencimiento-value">
                          {task.endDate}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Mostrar fecha de finalización cuando la tarea está completada */}
                  {task.status === 'done' && task.completedDate && (
                    <div className="task-section">
                      <div className="task-section-header">
                        <h5 className="task-section-title">
                          Fecha de Finalización
                        </h5>
                      </div>
                      <div className="task-section-content">
                        <p className="fecha-finalizacion-value">
                          {formatCompletedDate(task.completedDate)}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Mostrar tiempo restante para archivado en tareas completadas */}
                  {task.status === 'done' && (
                    <div className="task-section">
                      <div className="task-section-header">
                        <h5 className="task-section-title">
                          ⏳ Tiempo para Archivado
                        </h5>
                      </div>
                      <div className="task-section-content">
                        <div className="archival-info">
                          <p className="archival-time">
                            {(() => {
                              if (task.ArchivedDate) {
                                return "📦 Tarea ya archivada";
                              }
                              
                              const archivalInfo = getTimeUntilArchival(task);
                              console.log('Información de archivado para', task.title, ':', archivalInfo);
                              console.log('completedDate:', task.completedDate);
                              
                              if (archivalInfo) {
                                if (archivalInfo.shouldBeArchived) {
                                  return "🔄 Listo para archivar";
                                } else {
                                  return formatTimeUntilArchival(archivalInfo);
                                }
                              } else {
                                return `Sin fecha de finalización registrada`;
                              }
                            })()}
                          </p>
                          <p className="archival-explanation">
                            Las tareas se archivan automáticamente 72 horas después de ser completadas
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {task.createdBy && (
                    <div className="task-section">
                      <div className="task-section-header">
                        <h5 className="task-section-title">Creado por</h5>
                      </div>
                      <div className="task-section-content">
                        <p>{formattedCreatedBy}</p>
                      </div>
                    </div>
                  )}
                  
                  {canViewActivity && (
                    <div className="task-section">
                      <div className="task-section-content">
                        <div className="task-actions">
                          <button 
                            className="activity-button" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowActivityModal(!showActivityModal);
                            }}
                            title="Ver historial de actividades"
                          >
                            <span className="activity-button-icon">⏱️</span>
                            <span>Historial de Actividad</span>
                            <span className="activity-button-badge">{getTaskActivityCount()}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

                    {showEditForm && (
                <div className="task-form-overlay" onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!preventClose && !isUploading) {
                    closeEditModal();
                  }
                }}>
                  <div className="task-form-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="task-form-header">
                      <h3>Editar Tarea</h3>
                      {showSuccessMessage && (
                        <div className="success-indicator">
                          ✅ Guardado exitosamente
                        </div>
                      )}
                      <button 
                        className="close-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          closeEditModal();
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <TaskForm 
                      onComplete={() => {
                        // No cerrar automáticamente el formulario al editar
                        // Permitir al usuario cerrar manualmente si lo desea
                      }} 
                      onUpdateTask={handleUpdateTask}
                      taskToEdit={task}
                    />
                  </div>
                </div>
              )}

      {/* Modal para mostrar la actividad */}
      {showActivityModal && (
        <div className="activity-modal-overlay" onClick={() => setShowActivityModal(false)}>
          <div className="activity-modal" onClick={(e) => e.stopPropagation()}>
            <div className="activity-modal-header">
              <h3>
                <span className="activity-modal-icon">⏱️</span> 
                Historial de Actividad
              </h3>
              <button 
                className="close-btn"
                onClick={() => setShowActivityModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="activity-modal-content">
              <ActivityTimeline 
                task={task} 
                isVisible={true} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para eliminar tarea */}
      {showDeleteConfirmation && (
        <div className="task-form-overlay" onClick={cancelDelete}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-header">
              <h3>Confirmar eliminación</h3>
            </div>
            <div className="confirmation-content">
              <p>¿Estás seguro de que deseas marcar esta tarea como eliminada?</p>
            </div>
            <div className="confirmation-actions">
              <button 
                className="cancel-btn"
                onClick={cancelDelete}
              >
                Cancelar
              </button>
              <button 
                className="confirm-btn delete-confirm-btn"
                onClick={confirmDelete}
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para mover tarea entre tableros */}
      <MoveTaskModal
        task={task}
        isOpen={showMoveTaskModal}
        onClose={closeMoveTaskModal}
        onSuccess={handleMoveTaskSuccess}
      />

      {/* Modal para reasignar tarea */}
      {showReassignModal && (
        <ReassignTaskModal
          task={task}
          onClose={closeReassignModal}
          onReassign={handleReassign}
        />
      )}
    </>
  );
}

export default React.memo(TaskCard, (prevProps, nextProps) => {
  // Solo re-renderizar si cambian propiedades importantes de la tarea
  const prevTask = prevProps.task;
  const nextTask = nextProps.task;
  
  // Comparar propiedades clave que afectan la UI
  return (
    prevTask.id === nextTask.id &&
    prevTask.title === nextTask.title &&
    prevTask.description === nextTask.description &&
    prevTask.status === nextTask.status &&
    prevTask.assignee === nextTask.assignee &&
    JSON.stringify(prevTask.tags) === JSON.stringify(nextTask.tags) &&
    JSON.stringify(prevTask.files) === JSON.stringify(nextTask.files) &&
    JSON.stringify(prevTask.comments) === JSON.stringify(nextTask.comments) &&
    prevTask.endDate === nextTask.endDate &&
    prevTask.completedDate === nextTask.completedDate &&
    prevProps.onMoveTask === nextProps.onMoveTask &&
    prevProps.onDeleteTask === nextProps.onDeleteTask &&
    prevProps.onUpdateTask === nextProps.onUpdateTask
  );
}); 